import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Copy,
  Check,
  Download,
  FileText,
  Mail as MailIcon,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageTracking } from "../hooks/usePageTracking";
import { track } from "../lib/tracking";
import {
  generateAppealLetter,
  PLATFORM_OPTIONS,
  CATEGORY_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
} from "../lib/appealLetterTemplates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOTAL_STEPS = 4;

const downloadText = (filename, content) => {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const CopyButton = ({ text, label, testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        track("LetterCopied", { label });
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-medium text-slate-200 transition"
      data-testid={testid}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

export default function AppealLetterGeneratorPage() {
  usePageTracking("Appeal Letter Generator", { category: "tool" });
  const [step, setStep] = useState(0);
  const [platform, setPlatform] = useState("");
  const [category, setCategory] = useState("");
  const [accountType, setAccountType] = useState("");
  const [accountIdentifier, setAccountIdentifier] = useState("");
  const [noticeDate, setNoticeDate] = useState("");
  const [noticeText, setNoticeText] = useState("");
  const [priorAppeals, setPriorAppeals] = useState(null);
  const [revenueImpact, setRevenueImpact] = useState(null);
  const [userName, setUserName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [generated, setGenerated] = useState(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    document.title = "Free Appeal Letter Generator | Social Dispute Co";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Free tool: generate a structured first-draft appeal letter for your Instagram, Facebook, TikTok, YouTube, X, or Google Business Profile suspension. Plus an escalation letter and evidence checklist."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://socialdisputeco.com/letter");
  }, []);

  const canAdvance = useMemo(() => {
    if (step === 0) return !!platform;
    if (step === 1) return !!category;
    if (step === 2) return !!accountType;
    return true;
  }, [step, platform, category, accountType]);

  const generate = () => {
    const result = generateAppealLetter({
      platform,
      category,
      accountType,
      accountIdentifier,
      noticeDate,
      noticeText,
      priorAppeals,
      revenueImpact,
      userName,
      contactEmail,
    });
    setGenerated(result);
    track("LetterGenerated", { platform, category, accountType });
    setStep(TOTAL_STEPS);
    setTimeout(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!contactEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailSubmitting(true);
    setEmailError("");
    try {
      await axios.post(`${API}/triage`, {
        platform,
        suspensionType: "letter_generator",
        accountType,
        suspensionNoticeText: noticeText || null,
        classifiedCategory: category,
        classifiedSeverity: "letter_generator",
        recommendedTier: "letter_generator",
        email: contactEmail,
      });
      setEmailSubmitted(true);
      track("LetterLeadCaptured", { platform, category });
    } catch (err) {
      setEmailError("Couldn't save right now. Please try again.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  const renderProgress = () => (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div
          key={`progress-${i}`}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            i <= step ? "bg-blue-500" : "bg-white/10"
          }`}
        />
      ))}
    </div>
  );

  const renderChoice = (opts, value, setter, prefix) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {opts.map((o) => (
        <button
          type="button"
          key={o.id}
          onClick={() => setter(o.id)}
          className={`text-left p-4 rounded-xl border transition ${
            value === o.id
              ? "border-blue-500 bg-blue-500/10"
              : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/25"
          }`}
          data-testid={`${prefix}-${o.id}`}
        >
          <span className="text-sm font-medium text-white">{o.label}</span>
        </button>
      ))}
    </div>
  );

  // --- Result view -------------------------------------------------
  if (step === TOTAL_STEPS && generated) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Header />
        <main className="py-12 md:py-16" ref={previewRef}>
          <div className="mx-auto max-w-4xl px-6">
            <div className="mb-10" data-testid="letter-result-header">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/15 border border-green-500/30 text-green-300 text-xs font-medium mb-4">
                <Check className="w-3.5 h-3.5" />
                Draft generated
              </div>
              <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                Your appeal letter draft is ready.
              </h1>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Below is a structured first-draft appeal letter, a longer escalation letter for written escalation, and a tailored evidence checklist. Edit them to fit your specific case before submitting.
              </p>
            </div>

            {/* Honest disclaimer */}
            <div className="mb-8 rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-5 flex gap-3" data-testid="letter-disclaimer">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-100">
                <strong className="text-white">This is a first draft, not a final letter.</strong> Replace every <code className="text-yellow-300">[INSERT ...]</code> placeholder with specific information about your case. The more specific the appeal, the better the reviewer response. Outcomes are determined by the platform.
              </div>
            </div>

            {/* Appeal letter */}
            <section className="mb-8" data-testid="letter-appeal">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Appeal Letter (first draft)
                </h2>
                <div className="flex gap-2">
                  <CopyButton text={generated.appealLetter} label="appeal" testid="copy-appeal" />
                  <button
                    type="button"
                    onClick={() => downloadText(`appeal-letter-${platform}.txt`, generated.appealLetter)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-medium text-slate-200 transition"
                    data-testid="download-appeal"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap font-mono text-xs md:text-sm bg-slate-900 border border-white/10 rounded-xl p-5 text-slate-200 leading-relaxed overflow-x-auto">{generated.appealLetter}</pre>
            </section>

            {/* Escalation letter */}
            <section className="mb-8" data-testid="letter-escalation">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <MailIcon className="w-5 h-5 text-purple-400" />
                  Escalation Letter (for certified mail / legal contact)
                </h2>
                <div className="flex gap-2">
                  <CopyButton text={generated.escalationLetter} label="escalation" testid="copy-escalation" />
                  <button
                    type="button"
                    onClick={() => downloadText(`escalation-letter-${platform}.txt`, generated.escalationLetter)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-medium text-slate-200 transition"
                    data-testid="download-escalation"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Use this only AFTER the standard in-app appeal returned a denial or no response. See{" "}
                <Link to="/platform-registered-agent-addresses" className="text-blue-400 hover:underline">
                  platform legal mailing addresses
                </Link>
                .
              </p>
              <pre className="whitespace-pre-wrap font-mono text-xs md:text-sm bg-slate-900 border border-white/10 rounded-xl p-5 text-slate-200 leading-relaxed overflow-x-auto">{generated.escalationLetter}</pre>
            </section>

            {/* Evidence checklist */}
            <section className="mb-8 rounded-xl border border-white/10 bg-white/5 p-6" data-testid="letter-checklist">
              <h2 className="text-xl font-semibold mb-4">Evidence checklist</h2>
              <ul className="space-y-3">
                {generated.evidenceChecklist.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-300">
                    <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Email capture / lead magnet */}
            <section className="mb-8 rounded-xl border border-blue-500/30 bg-blue-500/5 p-6" data-testid="letter-email-capture">
              {emailSubmitted ? (
                <div className="text-center">
                  <Check className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <h3 className="text-xl font-semibold mb-2">Saved.</h3>
                  <p className="text-slate-300 text-sm">
                    If you'd like us to professionally prepare your appeal package, see{" "}
                    <Link to="/pricing" className="text-blue-400 hover:underline">
                      our service tiers
                    </Link>
                    .
                  </p>
                </div>
              ) : (
                <form onSubmit={submitEmail} className="space-y-3">
                  <h3 className="text-lg font-semibold">Save your draft and the case details</h3>
                  <p className="text-sm text-slate-400">
                    Optional. We'll save a record of this generation in case you'd like a polished, policy-mapped version prepared by us later. No marketing emails.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="flex-1 px-4 py-3 rounded-xl bg-slate-900 border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                      data-testid="letter-email-input"
                      required
                    />
                    <button
                      type="submit"
                      disabled={emailSubmitting}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-500 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-600 transition disabled:opacity-50"
                      data-testid="letter-email-submit"
                    >
                      {emailSubmitting ? "Saving..." : "Save my draft"}
                    </button>
                  </div>
                  {emailError && <p className="text-sm text-red-400">{emailError}</p>}
                </form>
              )}
            </section>

            {/* Upsell — professionally prepared */}
            <section className="mb-8 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8" data-testid="letter-upsell">
              <h3 className="text-xl font-semibold mb-3">Want a professionally prepared appeal package?</h3>
              <p className="text-slate-300 leading-relaxed mb-4">
                This draft is a starting point. Our $99 Case Review and $399 Appeal Package include human review, platform-specific policy mapping, evidence indexing, and submission through official channels with your authorization.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-6 py-3 text-sm font-semibold hover:bg-slate-100 transition"
                  data-testid="letter-upsell-pricing"
                >
                  See pricing
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/analyze"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium hover:bg-white/5 transition"
                >
                  Run the free Suspension Analyzer
                </Link>
              </div>
            </section>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep(0);
                  setGenerated(null);
                  setEmailSubmitted(false);
                }}
                className="text-sm text-slate-400 hover:text-white underline"
                data-testid="letter-restart"
              >
                Start a new letter
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // --- Step-by-step form -----------------------------------------------
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header />
      <main className="py-16 md:py-20">
        <div className="mx-auto max-w-2xl px-6">
          <div className="text-center mb-10" data-testid="letter-intro">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Free · No payment · Take 2 minutes
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              Free Appeal Letter Generator
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
              Answer 4 quick questions and we'll generate a structured first-draft appeal letter, a longer escalation letter for written escalation, and a tailored evidence checklist — all in plain text, ready to edit and submit.
            </p>
          </div>
          {renderProgress()}

          {step === 0 && (
            <section data-testid="letter-step-platform">
              <h2 className="text-xl font-semibold mb-2">Which platform suspended your account?</h2>
              <p className="text-sm text-slate-400 mb-6">Pick the closest match. Business Manager and Ad Account cases have different appeal paths.</p>
              {renderChoice(PLATFORM_OPTIONS, platform, setPlatform, "letter-platform")}
            </section>
          )}

          {step === 1 && (
            <section data-testid="letter-step-category">
              <h2 className="text-xl font-semibold mb-2">What kind of violation was cited?</h2>
              <p className="text-sm text-slate-400 mb-6">If you're not sure, pick the closest match. We'll structure the letter around the right policy framing.</p>
              {renderChoice(CATEGORY_OPTIONS, category, setCategory, "letter-category")}
            </section>
          )}

          {step === 2 && (
            <section data-testid="letter-step-account">
              <h2 className="text-xl font-semibold mb-2">What kind of account is it?</h2>
              <p className="text-sm text-slate-400 mb-6">This determines the evidence checklist and the tone of the letter.</p>
              {renderChoice(ACCOUNT_TYPE_OPTIONS, accountType, setAccountType, "letter-account")}
            </section>
          )}

          {step === 3 && (
            <section data-testid="letter-step-context">
              <h2 className="text-xl font-semibold mb-2">Case context (optional but recommended)</h2>
              <p className="text-sm text-slate-400 mb-6">The more we have, the more specific your draft will be. All fields are optional.</p>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Your full legal name (for the letter signature)</label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Jane Doe"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm"
                    data-testid="letter-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Account username / handle</label>
                  <input
                    type="text"
                    value={accountIdentifier}
                    onChange={(e) => setAccountIdentifier(e.target.value)}
                    placeholder="@yourhandle"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm"
                    data-testid="letter-handle-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date of the suspension notice</label>
                  <input
                    type="date"
                    value={noticeDate}
                    onChange={(e) => setNoticeDate(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-white/15 text-white focus:outline-none focus:border-blue-500 text-sm"
                    data-testid="letter-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Paste the suspension notice text</label>
                  <textarea
                    value={noticeText}
                    onChange={(e) => setNoticeText(e.target.value)}
                    rows={5}
                    placeholder={`The exact wording matters. Paste the platform's notice here if you have it.`}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm"
                    data-testid="letter-notice-textarea"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Have you previously appealed this?</label>
                  <div className="flex gap-3">
                    {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => (
                      <button
                        type="button"
                        key={String(o.v)}
                        onClick={() => setPriorAppeals(o.v)}
                        className={`px-5 py-2 rounded-lg border text-sm ${
                          priorAppeals === o.v
                            ? "border-blue-500 bg-blue-500/15 text-white"
                            : "border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"
                        }`}
                        data-testid={`letter-prior-${o.v ? "yes" : "no"}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Is revenue or business impacted?</label>
                  <div className="flex gap-3">
                    {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => (
                      <button
                        type="button"
                        key={String(o.v)}
                        onClick={() => setRevenueImpact(o.v)}
                        className={`px-5 py-2 rounded-lg border text-sm ${
                          revenueImpact === o.v
                            ? "border-blue-500 bg-blue-500/15 text-white"
                            : "border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"
                        }`}
                        data-testid={`letter-revenue-${o.v ? "yes" : "no"}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          <div className="mt-10 flex items-center justify-between">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"
                data-testid="letter-back"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              disabled={!canAdvance}
              onClick={() => {
                if (step < TOTAL_STEPS - 1) {
                  track("LetterStepCompleted", { step });
                  setStep((s) => s + 1);
                } else {
                  generate();
                }
              }}
              className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-6 py-3 text-sm font-semibold hover:bg-slate-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="letter-next"
            >
              {step === TOTAL_STEPS - 1 ? "Generate My Letter" : "Continue"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
